import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useContext } from "react";
import {
  Avatar,
  Box,
  Button as ButtonMui,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  ManageAccounts,
  SupervisorAccount,
  Block,
  Settings,
  Logout,
  ManageHistory,
  Wallet,
  BlockRounded,
  ManageSearchRounded,
  PostAddRounded,
  PersonSearchRounded,
  TuneRounded,
} from "@mui/icons-material";
import {
  getSessionUser,
  clearSessionUser,
  clearSessionToken,
} from "../../utils/users";
import { IUser } from "../Login/ILogin";
import { Empresas } from "../../utils/empresa";
import { Bar, Container, Content, Logo, UserContainer, Text } from "./style";
import logoPng from "../../assets/logo-azul.png";
import { avatarSurface } from "../../styles/tokens";
import { Button } from "../Button";
import { useUnsavedChanges } from "../../data/analiseContext";
import { apiOperation, headers } from "../../services/api";
import { Resultado } from "../../models/base/basic";
import { Conta } from "../../models/notouch/conta";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ShowChart from "@mui/icons-material/ShowChart";
import { set } from "date-fns";

let user: IUser | null = null;

export const Header: React.FC = () => {
  const [userLogged, setUserLogged] = useState<boolean>(false);

  const { hasUnsavedChanges } = useUnsavedChanges();

  useEffect(() => {
    user = getSessionUser();

    if (user) setUserLogged(true);
  }, []);

  return (
    <Container>
      <Content>
        {userLogged ? (
          <Logged hasUnsavedChanges={hasUnsavedChanges} />
        ) : (
          <NotLogged hasUnsavedChanges={hasUnsavedChanges} />
        )}
      </Content>
    </Container>
  );
};

interface NavigationProps {
  hasUnsavedChanges: boolean;
}

const NotLogged: React.FC<NavigationProps> = ({ hasUnsavedChanges }) => {
  const navigate = useNavigate();
  const currentLocation = useLocation();

  const handleLogoClick = () => {
    if (currentLocation.pathname === "/novapesquisa" && hasUnsavedChanges) {
      const confirmLeave = window.confirm(
        "É preciso clicar em analisar no fim da pagina para enviar o cnpj para análise. Caso saia agora os dados serão perdidos. Deseja sair?"
      );
      if (!confirmLeave) return;
    }
    navigate("/");
  };

  return (
    <>
      <div>
        <Logo onClick={handleLogoClick} src={logoPng} alt="Logo da CreditOn" />
      </div>
      <div>
        <Button className="login" onClick={() => navigate("/login")}>
          Login
        </Button>
      </div>
    </>
  );
};

const Logged: React.FC<NavigationProps> = ({ hasUnsavedChanges }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [carregaItensBrf, setCarregaItensBrf] = useState(false);
  const [userLogged, setUserLogged] = useState<boolean>(false);
  const [carregaItensBrt, setCarregaItensBrt] = useState(false);
  const [carregaItensAgenciaBrt, setcarregaItensAgenciaBrt] = useState(false);
  const [carregarItensTrombini, setCarregaItensTrombini] = useState(false);
  const currentLocation = useLocation();
  const [balance, setBalance] = useState<number>(0);
  const [carregaItensPetro, setCarregaItensPetro] = useState(false);
  const [roles, setRoles] = useState<string>("");

  useEffect(() => {
    user = getSessionUser();
    if (user) {
      setUserLogged(true);
    }

    if (user?.empresa === Empresas.Petrobahia) {
      setCarregaItensPetro(true);
    }

    if (user?.empresa === Empresas.Brf) {
      setCarregaItensBrf(true);
    }
    if (user?.empresa === Empresas.BRT) {
      setCarregaItensBrt(true);

      if (user?.perfil === "Agencia-brt") {
        setcarregaItensAgenciaBrt(true);
        getSaldo();
      }
    }

    if (user?.empresa === Empresas.Trombini) {
      setCarregaItensTrombini(true);
    }

    setRoles(user?.cargosUsuario || "");
  }, []);

  const handleLogoClick = () => {
    if (currentLocation.pathname === "/novapesquisa" && hasUnsavedChanges) {
      const confirmLeave = window.confirm(
        "É preciso clicar em analisar no fim da pagina para enviar o cnpj para análise. Caso saia agora os dados serão perdidos. Deseja sair?"
      );
      if (!confirmLeave) return;
    }
    navigate("/historico");
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Escolhe o fundo do avatar dentro da paleta.
   *
   * A versão anterior derivava um RGB arbitrário do hash do nome, o que produzia
   * cores fora da marca e sem contraste garantido com a inicial. Aqui o hash apenas
   * seleciona uma entre as cores institucionais que já passam AA com texto branco —
   * o usuário continua tendo sempre a mesma cor, mas dentro do design system.
   */
  function avatarColor(name: string) {
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return avatarSurface[Math.abs(hash) % avatarSurface.length];
  }

  function stringAvatar(name: string) {
    return {
      sx: {
        bgcolor: avatarColor(name),
        color: "var(--surface-raised)",
      },
      children: `${name[0]}`,
    };
  }

  const logoutUser = () => {
    clearSessionUser();
    clearSessionToken();
    navigate("/home");
  };

  const userProfile = user?.perfil;

  const getSaldo = async () => {
    try {
      const config = {
        headers: headers(),
      };
      let urlString = `notouch/conta/obter`;

      const { data } = await apiOperation.get<Resultado<Conta>>(
        urlString,
        config
      );

      if (!data.erro) {
        setBalance(data.valor.saldo);
      } else {
        setBalance(0);
      }
    } catch (error: any) {
      setBalance(0);
    }
  };

  return (
    <>
      <div>
        <Logo onClick={handleLogoClick} src={logoPng} />
      </div>
      <UserContainer>
        <div>
          <Tooltip title="Configurações da Conta">
            <ButtonMui
              onClick={handleClick}
              size="small"
              sx={{ minWidth: "240px" }}
              aria-controls={open ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              endIcon={<KeyboardArrowDownIcon />}
            >
              <Avatar {...stringAvatar(user?.nomeUsuario || "")} />
              <Typography>{user?.nomeUsuario}</Typography>
            </ButtonMui>
          </Tooltip>
          <Bar />
          <Text style={{ fontSize: "24px", display: "none" }}>15</Text>
          <Text style={{ display: "none" }}>Consultas Disponíveis</Text>
        </div>
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                maxHeight: "80vh",
                overflowY: "auto",
                filter: "drop-shadow(var(--elevation-2))",
                mt: 0,
                "& .MuiAvatar-root": {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                "&:before": {
                  display: "none",
                },
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem>
            <ListItemIcon>
              <ManageAccounts fontSize="small" />
            </ListItemIcon>
            <ListItemText
              onClick={() =>
                carregaItensAgenciaBrt
                  ? navigate("/conta")
                  : navigate("/perfil")
              }
            >
              Minha Conta
            </ListItemText>
          </MenuItem>
          {carregaItensAgenciaBrt && <Divider />}
          {carregaItensAgenciaBrt && (
            <MenuItem>
              <ListItemIcon>
                <Wallet fontSize="small" />
              </ListItemIcon>
              <ListItemText onClick={() => navigate("/carteira")}>
                Minha Carteira
              </ListItemText>
            </MenuItem>
          )}
          <Divider />
          <MenuItem>
            <ListItemIcon>
              <ManageHistory fontSize="small" />
            </ListItemIcon>
            <ListItemText onClick={() => navigate("/gerenciarpesquisa")}>
              Relatório de Utilização
            </ListItemText>
          </MenuItem>
          {!carregaItensAgenciaBrt && <Divider />}
          {!carregaItensAgenciaBrt && (
            <MenuItem>
              <ListItemIcon>
                <ManageHistory fontSize="small" />
              </ListItemIcon>
              <ListItemText onClick={() => navigate("/resumo-porte")}>
                Resumo de dados
              </ListItemText>
            </MenuItem>
          )}
          {(userProfile === "Admin" || userProfile === "Master") && (
            <>
              <Divider />
              <MenuItem>
                <ListItemIcon>
                  <ManageAccounts fontSize="small" />
                </ListItemIcon>
                <ListItemText onClick={() => navigate("/preview")}>
                  Esteira
                </ListItemText>
              </MenuItem>
            </>
          )}
          {userProfile === "Admin" && <Divider />}
          {userProfile === "Admin" && (
            <MenuItem>
              <ListItemIcon>
                <SupervisorAccount fontSize="small" />
              </ListItemIcon>
              <ListItemText onClick={() => navigate("/usuarios")}>
                Usuários
              </ListItemText>
            </MenuItem>
          )}
          {(roles.includes("ACESSOTOTALCREDITON") ||
            roles.includes("ACESSOADMINEMPRESA")) && (
            <>
              <Divider />
              <MenuItem>
                <ListItemIcon>
                  <ApartmentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText onClick={() => navigate("/unidade-empresa")}>
                  Unidades
                </ListItemText>
              </MenuItem>
            </>
          )}
          <MenuItem style={{ display: "none" }}>
            <ListItemIcon>
              <Block fontSize="small" />
            </ListItemIcon>
            <ListItemText onClick={() => navigate("/empresas-bloqueadas")}>
              Lista de bloqueio
            </ListItemText>
          </MenuItem>
          <MenuItem style={{ display: "none" }}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            <ListItemText onClick={() => navigate("/configuracao")}>
              Configurações
            </ListItemText>
          </MenuItem>
          {userProfile === "Admin" && carregaItensBrt && (
            <MenuItem>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              <ListItemText
                onClick={() => {
                  navigate("/analisebrt");
                }}
              >
                Análise de Crédito
              </ListItemText>
            </MenuItem>
          )}
          {<Divider />}
          {
            <MenuItem>
              <ListItemIcon>
                <GroupAddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                onClick={() => {
                  navigate("/import-planilha");
                }}
              >
                Classificação Relacionamento Cliente
              </ListItemText>
            </MenuItem>
          }
          {<Divider />}
          {
            <MenuItem>
              <ListItemIcon>
                <PersonSearchRounded fontSize="small" />
              </ListItemIcon>
              <ListItemText
                onClick={() => {
                  navigate("/relacionamento-cliente");
                }}
              >
                Historico Relacionamento Cliente
              </ListItemText>
            </MenuItem>
          }
          {<Divider />}
          {
            <MenuItem>
              <ListItemIcon>
                <TuneRounded fontSize="small" />
              </ListItemIcon>
              <ListItemText
                onClick={() => {
                  navigate("/parametrizacao");
                }}
              >
                Parametrização
              </ListItemText>
            </MenuItem>
          }
          {!carregaItensAgenciaBrt && <Divider />}
          {!carregaItensAgenciaBrt && (
            <MenuItem>
              <ListItemIcon>
                <BlockRounded fontSize="small" />
              </ListItemIcon>
              <ListItemText
                onClick={() => {
                  navigate("/block-list");
                }}
              >
                Lista de bloqueio
              </ListItemText>
            </MenuItem>
          )}
          {!carregaItensAgenciaBrt && <Divider />}
          {!carregaItensAgenciaBrt && (
            <MenuItem>
              <ListItemIcon>
                <PostAddRounded fontSize="small" />
              </ListItemIcon>
              <ListItemText
                onClick={() => {
                  navigate("/analise-lote");
                }}
              >
                Análise em Lote
              </ListItemText>
            </MenuItem>
          )}
          {!carregaItensAgenciaBrt && <Divider />}
          {!carregaItensAgenciaBrt && (
            <MenuItem>
              <ListItemIcon>
                <ManageSearchRounded fontSize="small" />
              </ListItemIcon>
              <ListItemText
                onClick={() => {
                  navigate("/historico-lote");
                }}
              >
                Histórico Lote
              </ListItemText>
            </MenuItem>
          )}
          {carregarItensTrombini && (
            <>
              <Divider />
              <MenuItem>
                <ListItemIcon>
                  <ShowChart fontSize="small" />
                </ListItemIcon>
                <ListItemText onClick={() => navigate("/monitoramento")}>
                  Monitoramento Serasa
                </ListItemText>
              </MenuItem>
            </>
          )}
          <Divider />
          {carregaItensPetro && (
            <>
              <MenuItem>
                <ListItemIcon>
                  <SupervisorAccount fontSize="small" />
                </ListItemIcon>
                <ListItemText onClick={() => navigate("/dados-cliente")}>
                  Dados do Cliente
                </ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem>
                <ListItemIcon>
                  <PostAddRounded fontSize="small" />
                </ListItemIcon>
                <ListItemText onClick={() => navigate("/lista-sumula")}>
                  Lista de Sumulas
                </ListItemText>
              </MenuItem>
              <Divider />
            </>
          )}
          <MenuItem>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            <ListItemText onClick={logoutUser}>Sair</ListItemText>
          </MenuItem>
        </Menu>
        {carregaItensAgenciaBrt && (
          <MenuItem
            onClick={() => navigate("/carteira")}
            sx={{ borderRadius: "10px", padding: "2px" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexDirection: "column",
              }}
            >
              <Text style={{ fontSize: "20px", padding: "4px 0px" }}>
                {balance}
              </Text>
              <Text style={{ fontSize: "14px" }}>Saldo</Text>
            </Box>
          </MenuItem>
        )}
      </UserContainer>
    </>
  );
};
